<div class="contact-page">
  <!-- =========================================================
       Breadcrumb
   ========================================================== -->
  <nav class="breadcrumb" aria-label="Breadcrumb">
    <a class="breadcrumb__home" routerLink="/">
      <span aria-hidden="true">⌂</span>
    </a>
    <span class="breadcrumb__divider">/</span>
    <span class="breadcrumb__current">Contact</span>
  </nav>

  <!-- =========================================================
       Hero
   ========================================================== -->
  <section class="contact-hero">
    <div class="contact-hero__background" aria-hidden="true">
      <span class="hero-orb hero-orb--one"></span>
      <span class="hero-orb hero-orb--two"></span>
      <span class="hero-grid"></span>
    </div>

    <div class="contact-hero__content">
      <span class="hero-pill">
        <span class="hero-pill__dot"></span>
        Get in touch
      </span>

      <h1>We would love to hear from you.</h1>

      <p>
        Questions about a booking, your account, or getting your salon listed?
        Send us a note and the gloget team will point you in the right
        direction.
      </p>

      <div class="contact-hero__meta">
        <span>Replies within one business day</span>
        <span class="contact-hero__meta-dot"></span>
        <span>Mon–Sat · 9am–6pm</span>
      </div>
    </div>
  </section>

  <!-- =========================================================
       Content
   ========================================================== -->
  <section class="contact-content">
    <div class="contact-layout">
      <!-- =====================================================
           Sidebar
       ====================================================== -->
      <aside class="contact-sidebar">
        <div class="contact-side-card">
          <span class="section-kicker">Direct lines</span>
          <h2>Prefer to reach out directly?</h2>
          <p>
            Pick whichever is easiest — the same friendly team is behind all of
            them.
          </p>

          <div class="contact-methods">
            <div class="contact-method">
              <span class="contact-method__icon" aria-hidden="true">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <rect x="3" y="5" width="18" height="14" rx="2"></rect>
                  <path d="m3 7 9 6 9-6"></path>
                </svg>
              </span>
              <div>
                <small>Email</small>
                <a [href]="'mailto:' + supportEmail">{{ supportEmail }}</a>
              </div>
            </div>

            <div class="contact-method">
              <span class="contact-method__icon" aria-hidden="true">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path
                    d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.6A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.4-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z"
                  ></path>
                </svg>
              </span>
              <div>
                <small>Phone</small>
                <a [href]="supportPhoneHref">{{ supportPhoneDisplay }}</a>
              </div>
            </div>

            <div class="contact-method">
              <span class="contact-method__icon" aria-hidden="true">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path
                    d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
                  ></path>
                </svg>
              </span>
              <div>
                <small>Live chat</small>
                <span>Available in the app</span>
              </div>
            </div>
          </div>

          <div class="support-note">
            <p>
              About an existing booking? Include your booking reference so we
              can pick it up right away.
            </p>
          </div>
        </div>
      </aside>

      <!-- =====================================================
           Form
       ====================================================== -->
      <div class="contact-form-wrap">
        <div class="contact-form-card">
          <div class="contact-form-card__head">
            <span class="section-kicker">Send a message</span>
            <h2>Tell us how we can help.</h2>
          </div>

          <!-- Success state -->
          <div *ngIf="isSent; else contactFormTpl" class="contact-success">
            <div class="contact-success__icon" aria-hidden="true">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M20 6 9 17l-5-5"></path>
              </svg>
            </div>

            <h3>Thanks — your message is on its way.</h3>
            <p>
              We have received your message and will get back to you at the
              contact details you provided.
            </p>

            <div class="contact-success__actions">
              <button type="button" class="btn-primary" (click)="resetForm()">
                Send another message
              </button>
              <a routerLink="/faq" class="btn-ghost">Back to FAQs</a>
            </div>
          </div>

          <ng-template #contactFormTpl>
            <form
              [formGroup]="contactForm"
              (ngSubmit)="submit()"
              novalidate
              class="contact-form"
            >
              <div class="contact-row">
                <div class="field" [class.field--invalid]="isInvalid('name')">
                  <label for="c-name">
                    Full name <span class="req" aria-hidden="true">*</span>
                  </label>
                  <input
                    id="c-name"
                    type="text"
                    placeholder="Jordan Smith"
                    autocomplete="name"
                    formControlName="name"
                  />
                  <p *ngIf="isInvalid('name')" class="field__error">
                    Please enter your name.
                  </p>
                </div>

                <div
                  class="field"
                  [class.field--invalid]="isInvalid('email')"
                >
                  <label for="c-email">
                    Email <span class="req" aria-hidden="true">*</span>
                  </label>
                  <input
                    id="c-email"
                    type="email"
                    placeholder="you@example.com"
                    autocomplete="email"
                    formControlName="email"
                  />
                  <p *ngIf="isInvalid('email')" class="field__error">
                    Please enter a valid email address.
                  </p>
                </div>
              </div>

              <div class="contact-row">
                <div class="field">
                  <label for="c-phone">
                    Phone <span class="opt">(optional)</span>
                  </label>
                  <input
                    id="c-phone"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    autocomplete="tel"
                    formControlName="phone"
                  />
                </div>

                <div class="field">
                  <label for="c-audience">I am a…</label>
                  <select id="c-audience" formControlName="audience">
                    <option *ngFor="let a of audiences" [value]="a.key">
                      {{ a.label }}
                    </option>
                  </select>
                </div>
              </div>

              <div class="field" [class.field--invalid]="isInvalid('subject')">
                <label for="c-subject">
                  Subject <span class="req" aria-hidden="true">*</span>
                </label>
                <input
                  id="c-subject"
                  type="text"
                  placeholder="e.g. Booking help, partnership, feedback"
                  formControlName="subject"
                />
                <p *ngIf="isInvalid('subject')" class="field__error">
                  Please add a short subject.
                </p>
              </div>

              <div
                class="field"
                [class.field--invalid]="isInvalid('message')"
              >
                <label for="c-message">
                  Message <span class="req" aria-hidden="true">*</span>
                </label>
                <textarea
                  id="c-message"
                  placeholder="How can we help?"
                  formControlName="message"
                ></textarea>
                <p *ngIf="isInvalid('message')" class="field__error">
                  Please tell us a little more (at least 10 characters).
                </p>
              </div>

              <div class="field">
                <span class="field__label">Preferred way to reach you</span>
                <div
                  class="radio-group"
                  role="radiogroup"
                  aria-label="Preferred contact method"
                >
                  <label
                    *ngFor="let m of preferredMethods"
                    class="radio-option"
                  >
                    <input
                      type="radio"
                      [value]="m.key"
                      formControlName="preferred"
                    />
                    <span>{{ m.label }}</span>
                  </label>
                </div>
              </div>

              <div class="contact-form__submit">
                <span class="contact-form__note">
                  Fields marked * are required.
                </span>
                <button
                  type="submit"
                  class="btn-primary"
                  [disabled]="isSending"
                >
                  <span *ngIf="isSending" class="spinner" aria-hidden="true">
                  </span>
                  {{ isSending ? "Sending…" : "Send message" }}
                </button>
              </div>
            </form>
          </ng-template>
        </div>
      </div>
    </div>
  </section>

  <!-- =========================================================
       Bottom CTA
   ========================================================== -->
  <section class="contact-bottom-cta">
    <div>
      <span class="section-kicker">Not sure where to start?</span>
      <h2>Answers are probably in our help centre.</h2>
      <p>
        Browse the most common questions about booking, payments, your account,
        and getting your salon listed.
      </p>
    </div>

    <a routerLink="/faq" class="contact-bottom-cta__button">
      Visit the FAQs
      <span>→</span>
    </a>
  </section>
</div>
